import React, { useEffect } from 'react';

import { usePaymentMethodStore, useUserMessageStore } from '../../../stores';
import { useStripeClient } from '../../../hooks/useStripeClient';

import CircularProgress from '@mui/material/CircularProgress';
import { Box, Button, Checkbox, FormControlLabel, Typography } from '@mui/material';
import { InfoOutlined } from '@mui/icons-material';
import { Elements, PaymentElement, useElements, useStripe } from '@stripe/react-stripe-js';

interface SetupPaymentMethodWrapperProps {
  redirectUrl: string;
  onSave: () => void;
  onCancel: () => void;
}

function SetupPaymentMethodWrapper({ redirectUrl, onSave, onCancel }: SetupPaymentMethodWrapperProps) {
  const [isAutopay, setIsAutopay] = React.useState(false);
  const [isLoading, setIsLoading] = React.useState(false);
  const [showActionButtons, setShowActionButtons] = React.useState(false);
  const setUserMessage = useUserMessageStore.use.setUserMessage();
  const setupIntentId = usePaymentMethodStore.use.setupIntentId();
  const updateMethodIntent = usePaymentMethodStore.use.updateMethodIntent();

  const stripe = useStripe();
  const elements = useElements();

  useEffect(() => {
    if (showActionButtons && setupIntentId) {
      setIsLoading(true);
      updateMethodIntent({ setupIntentId, isAutopay }).finally(() => setIsLoading(false));
    }
  }, [isAutopay]);

  const handleSaveClick = async (e: React.FormEvent<HTMLButtonElement>) => {
    e.preventDefault();

    if (!stripe || !elements) {
      return;
    }

    setIsLoading(true);
    try {
      const { error, setupIntent } = await stripe.confirmSetup({
        elements,
        confirmParams: { return_url: redirectUrl },
        redirect: 'if_required',
      });

      if (error) {
        setUserMessage(error.message as string, 'error');
        setShowActionButtons(true);
      } else {
        const newMethodId = setupIntent?.payment_method as string | null;
        if (newMethodId) {
          await updateMethodIntent({ setupIntentId: setupIntent.id, isAutopay, paymentMethodId: newMethodId });
        }
        onSave();
      }
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <>
      <PaymentElement id="payment-element" onReady={() => setShowActionButtons(true)} />
      {showActionButtons && (
        <Box>
          <FormControlLabel
            label="Set to AutoPay"
            disabled={isLoading}
            control={<Checkbox checked={isAutopay} onChange={() => setIsAutopay(!isAutopay)} />}
          />
          <Box style={{ display: 'flex', gap: '.25rem' }}>
            <InfoOutlined sx={{ fontSize: 20, color: (theme) => theme.palette.action.active }}></InfoOutlined>
            <span>Only one payment method can be set to AutoPay</span>
          </Box>
          <Box style={{ textAlign: 'right', marginTop: '5px' }}>
            <Button
              sx={{ marginRight: 2 }}
              disabled={isLoading}
              onClick={(e: any) => {
                e.preventDefault();
                onCancel();
              }}
            >
              Cancel
            </Button>
            <Button disabled={isLoading} onClick={handleSaveClick}>
              {isLoading && (
                <CircularProgress
                  size={14}
                  sx={{
                    color: (theme) => theme.palette.secondary.main,
                  }}
                />
              )}
              Save
            </Button>
          </Box>
        </Box>
      )}
    </>
  );
}

interface AddPaymentMethodProps {
  onSave: () => void;
  onCancel: () => void;
}

export default function AddPaymentMethod({ onSave, onCancel }: AddPaymentMethodProps) {
  const clientSecret = usePaymentMethodStore.use.clientSecret();
  const stripeClient = useStripeClient();

  return (
    <>
      {clientSecret && stripeClient && (
        <>
          <Typography mt={1} variant="h4">
            Add Payment Method
          </Typography>
          <Typography mt={3} mb={4} variant="body1" sx={{ color: (theme) => theme.palette.action.active }}>
            Adding your payment method is completely secure and removes the hassle of entering payment details for each invoice.
          </Typography>
          <Elements
            options={{
              clientSecret,
              appearance: { theme: 'stripe' },
            }}
            stripe={stripeClient}
          >
            <SetupPaymentMethodWrapper redirectUrl={`https://${location.hostname}/payment-methods`} onSave={onSave} onCancel={onCancel} />
          </Elements>
        </>
      )}
    </>
  );
}
